/**
 * Onboarding Welcome Component
 * First-time user welcome screen with journey selection
 */

import React, { useState } from 'react';
import { useTutorial } from '@/context/TutorialContext';

import { secureLogger } from '@/utils/secureLogger';
const OnboardingWelcome: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { setFirstTimeUserComplete } = useTutorial();
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  const roles = [
    {
      id: 'project-manager',
      title: 'Project Manager',
      icon: 'PM',
      description: 'Manage projects and track your IT assets',
      journey: 'project-manager-onboarding',
    },
    {
      id: 'change-manager',
      title: 'Change Manager',
      icon: 'CM',
      description: 'Handle change requests and approvals',
      journey: 'change-manager-workflow',
    },
    {
      id: 'security-analyst',
      title: 'Security Analyst',
      icon: 'SA',
      description: 'Manage security scans and vulnerabilities',
      journey: 'security-analyst-workflow',
    },
    {
      id: 'developer',
      title: 'Developer',
      icon: 'DEV',
      description: 'Collaborate on deployments and incident management',
      journey: 'developer-collaboration',
    },
  ];

  const handleStart = (roleId: string) => {
    setSelectedRole(roleId);
    // Route to journey based on role
    const selectedRoleData = roles.find((r) => r.id === roleId);
    if (selectedRoleData) {
      // In a real app, this would navigate to the journey
      secureLogger.debug('Starting journey:', selectedRoleData.journey);
      setFirstTimeUserComplete();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-[min(48rem,calc(100vw-2rem))] w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 sm:p-6 text-white">
          <h1 className="text-2xl sm:text-4xl font-bold mb-2">Welcome to SDLC Tracker!</h1>
          <p className="text-base sm:text-xl text-blue-100">Let's get you started with the right path for your role</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">What's your role?</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              We'll personalize your experience based on what you do. You can always change this later.
            </p>

            {/* Role Selection Grid */}
            <div className="grid md:grid-cols-2 gap-3 mb-8">
              {roles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => setSelectedRole(role.id)}
                  className={`p-5 rounded-xl border-2 transition-all text-left ${
                    selectedRole === role.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 bg-white dark:bg-gray-800'
                  }`}
                >
                  <div className="text-4xl mb-3">{role.icon}</div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{role.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{role.description}</p>

                  {selectedRole === role.id && (
                    <div className="mt-4 text-blue-600 dark:text-blue-400 font-semibold flex items-center gap-2">
                      OK Selected
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Key Features */}
            <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-xl mb-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">What you'll learn:</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex gap-3">
                  <span className="text-blue-500">OK</span>
                  <span>How to navigate the application</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-500">OK</span>
                  <span>Complete step-by-step workflows for your role</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-500">OK</span>
                  <span>Access to glossary and help documentation</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-500">OK</span>
                  <span>Tips and best practices from the community</span>
                </li>
              </ul>
            </div>

            {/* Video/Demo Section */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 p-5 rounded-xl mb-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-3">Watch a quick demo</h3>
              <div className="aspect-video bg-gray-300 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                <span className="text-gray-600 dark:text-gray-400">Demo video coming soon</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-between items-center">
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium"
            >
              Skip for Now
            </button>

            <button
              onClick={() => {
                if (selectedRole) {
                  handleStart(selectedRole);
                }
              }}
              disabled={!selectedRole}
              className={`px-7 py-3 rounded-lg font-medium transition-colors ${
                selectedRole
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              Start My Journey
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWelcome;
